import { Injectable, Injector } from '@angular/core';
import { MtmAuthBridgeService } from '@mtm/auth-ui-angular';
import { Observable, concatMap, map, of } from 'rxjs';
import { ApiService } from './api.service';
import { HelperService } from './helper.service';
import { EmitterService } from './emitter.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { contentLanguageUpdated } from './notification.service';
import { Brand, SimpleCompany, User, ProfileSettings } from '../interfaces';
import { LogService } from './log.service';
import { AuthTokenUpdatedEvent, AuthUserUpdatedEvent, BrandUpdatedEvent, UserLoggedOutEvent } from '../models/events';
import { UserflowService } from './userflow.service';
import { environment } from '../../../environments/environment';
import { AuthPermissionsService } from './auth-permissions.service';
import { EffectivePermissions } from '../interfaces/auth-permissions.interface';
import { LoginResponse, TwoFactorResponse, PasswordResetResponse, LogoutOptions } from '../interfaces/auth.interface';
import { clearBrowserStorageKeepingPreferences } from '../utils/browser-storage.utils';
import {
  IMPERSONATION_ORIGIN_STORAGE_KEY,
  APP_TOKEN_STORAGE_KEY,
  LAST_VISITED_BRAND_STORAGE_KEY
} from '../constants';
import { currentAppToken } from '../auth/app-token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private readonly apiService: ApiService,
    private readonly helperService: HelperService,
    private readonly logService: LogService,
    private readonly userflowService: UserflowService,
    private readonly authPermissionsService: AuthPermissionsService,
    private readonly injector: Injector
  ) {}

  private proceedLogin(data: LoginResponse): Observable<TwoFactorResponse> {
    this.setAuthUser(data.user);
    return this.setAuthTokenWithTests(data.auth.token).pipe(
      map(() => {
        this.setAuthSession();

        if (data.userProfileSettings) {
          this.setAuthUserSettings(data.userProfileSettings);
          this.applyDefaultBrand(data.userProfileSettings);
          if (data.userProfileSettings.language) {
            this.setAuthLanguage(data.userProfileSettings.language);
          }
        }
        return { challenge: false };
      })
    );
  }

  private setAuthTokenWithTests(token: string): Observable<EffectivePermissions> {
    return this.setAuthToken(token);
  }

  public verify2fa(email: string, code: string, mechanism: string): Observable<TwoFactorResponse> {
    const endpoint: string = `/api/auth/${email}/verification/${code}/${mechanism}`;
    return this.apiService.httpPost(endpoint, {}).pipe(
      concatMap((data: LoginResponse) => {
        clearBrowserStorageKeepingPreferences();
        if (data.user?.brandId) {
          localStorage.setItem('brandId', data.user.brandId);
        }
        return this.proceedLogin(data);
      })
    );
  }

  public newLogin(email: string, password: string, captcha: string): Observable<TwoFactorResponse> {
    const body: { email: string; password: string } = {
      email: email,
      password: password,
    };
    return this.apiService
      .httpPost('/api/auth', body, null, null, { 'g-captcha': captcha }, { skipErrorHandling: true })
      .pipe(
        concatMap((data: LoginResponse) => {
          if (data.user?.brandId) {
            localStorage.setItem('brandId', data.user.brandId);
          }
          const { twoFa = false, twoFaMechanism = '' } = data;
          if (!twoFa) {
            return this.proceedLogin(data);
          }
          this.changeToken(data.auth.token);
          return of({ challenge: true, mechanism: twoFaMechanism });
        })
      );
  }

  public changeToken(token: string): void {
    this.setAuthSession();
    this.setAuthToken(token);
  }

  public logout(options: LogoutOptions | null = null): boolean {
    // Run server/SDK logout only for an active session. localStorage.clear() below is synchronous and
    // precedes the caller's navigate to /entry, so NoAuthGuard's re-invocation finds no token and skips —
    // exactly one logout request, no in-flight flag needed.
    if (this.getAuthToken()) {
      // Delegate to the IAM SDK: clears local session state, stops the token-renewal timer, and
      // calls POST /api/v1/auth/logout on the IAM (withCredentials clears the HttpOnly cookie).
      // Lazy injector lookup avoids the documented bootstrap DI cycle (AuthService -> bridge -> HttpClient).
      this.injector
        .get(MtmAuthBridgeService)
        .logout$()
        .subscribe({ error: () => {} });
    }

    clearBrowserStorageKeepingPreferences();
    this.logService.setUser(null);

    EmitterService.get(UserLoggedOutEvent).emit(null);

    // logout userflow
    if (environment.userflow.token) {
      this.userflowService.reset();
    }

    return true;
  }

  public getAuthUserName(): string | null {
    let authUser: User | null = this.getAuthUser();
    if (!authUser) {
      const registerDataStr: string | null = localStorage.getItem('register_data');
      if (registerDataStr) {
        const registerData: { user: { email: string } } = JSON.parse(registerDataStr);
        authUser = { username: registerData.user.email } as User;
      }
    }
    // The IAM SDK shares the `auth_user` localStorage key and can overwrite the legacy user with its
    // identity, which carries `email` but no legacy `username`. For IAM/SSO users username === email
    // (the gateway accepts the email as the `/api/users/{username}` path param), so fall back to
    // email to avoid `/api/users/null/...` requests.
    return authUser?.username ?? authUser?.email ?? null;
  }

  public getAuthUser(): User | null {
    const user: string | null = localStorage.getItem('auth_user');
    return user ? JSON.parse(user) : null;
  }

  public getAuthUserSettings(): ProfileSettings | null {
    const userSettings: string | null = localStorage.getItem('auth_user_settings');
    return userSettings ? (JSON.parse(userSettings) as ProfileSettings) : null;
  }

  public getAuthToken(): string | null {
    return currentAppToken() ?? null;
  }

  public getAuthSession(): string | null {
    return localStorage.getItem('auth_session') ?? null;
  }

  public getUserBrand(): Brand | null {
    const brandValue: string | null = localStorage.getItem('brand');
    return brandValue ? JSON.parse(brandValue) : null;
  }

  public isTokenExpired(): boolean {
    const token: string | null = currentAppToken();
    if (token) {
      const jwtHelper: JwtHelperService = new JwtHelperService();
      return jwtHelper.isTokenExpired(token);
    }
    return false;
  }

  /**
   * True while an admin is acting on behalf of another user ("switch user"). A switch started in
   * this app stashes the original admin token under `admin_token`; one handed over by the platform
   * admin console may have no session here to stash, and is marked by its origin instead.
   */
  public isImpersonating(): boolean {
    return (
      !!localStorage.getItem('admin_token') || !!localStorage.getItem(IMPERSONATION_ORIGIN_STORAGE_KEY)
    );
  }

  public setAuthUser(user: User): void {
    if (!user.companyId) {
      return;
    }
    if (user.avatarUrl) {
      user.avatarUrl = this.helperService.getFixedAvatarUrl(user.avatarUrl, this.getAuthSession());
    }

    localStorage.setItem('auth_user', JSON.stringify(user));

    if (environment.userflow.token) {
      if (user.email) {
        this.userflowService.identify(user.email, {
          name: user.fullName ?? '',
          email: user.email,
          signed_up_at: user.activationDate ? new Date(user.activationDate).toISOString() : new Date().toISOString(),
        });
      }
    }
    this.logService.setUser(user?.username || null);
    EmitterService.get(AuthUserUpdatedEvent).emit(user);
  }

  public updateAuthUserCompany(newCompany: SimpleCompany): void {
    const authUser: User | null = this.getAuthUser();
    if (!authUser?.company) {
      return;
    }

    const oldCompany: SimpleCompany = authUser.company;

    if (
      oldCompany.id === newCompany.id &&
      oldCompany.name === newCompany.name &&
      oldCompany.description === newCompany.description &&
      oldCompany.companyType === newCompany.companyType
    ) {
      return;
    }

    authUser.company = newCompany;
    authUser.companyId = newCompany.id;
    this.setAuthUser(authUser);
    EmitterService.get('authUser.updateCompany').emit();
  }

  public setAuthUserSettings(userSettings: ProfileSettings): void {
    localStorage.setItem('auth_user_settings', JSON.stringify(userSettings));
  }

  /**
   * Seeds the stored brand from the user's Account Preferences choice, so the first page after
   * login opens on their default brand. Login-time only: `brandId` is the app's "current brand"
   * and an in-session switch must keep winning over the stored preference.
   */
  public applyDefaultBrand(userSettings: ProfileSettings | null): void {
    if (userSettings?.defaultBrandId) {
      localStorage.setItem('brandId', userSettings.defaultBrandId);
    }
  }

  /**
   * Persist the token without side effects (no `AuthTokenUpdatedEvent`, no permission fetch).
   * Used to authorize the user-hydration request so the user is loaded/stored BEFORE permissions
   * and feature config are fetched.
   */
  public setAuthTokenValue(token: string): void {
    localStorage.setItem(APP_TOKEN_STORAGE_KEY, token);
  }

  public setAuthToken(token: string): Observable<EffectivePermissions> {
    localStorage.setItem(APP_TOKEN_STORAGE_KEY, token);
    EmitterService.get(AuthTokenUpdatedEvent).emit(token);
    // A new token can mean a new user or new grants — never reuse the TTL cache here.
    return this.authPermissionsService.updateEffectivePermissions(true);
  }

  public setAuthSession(): void {
    const timestamp: string = String(Date.now());
    localStorage.setItem('auth_session', timestamp);
  }

  public requestVerificationSendPasswordToken(email: string): Observable<unknown> {
    return this.injector.get(MtmAuthBridgeService).requestResetPassword$(email);
  }

  public verifyUserPasswordToken(token: string): Observable<PasswordResetResponse> {
    return this.injector
      .get(MtmAuthBridgeService)
      .validateResetPasswordToken$(token)
      .pipe(
        map((user) => ({
          firstName: user.firstName,
          lastName: user.lastName,
          token,
          companyId: '',
        }))
      );
  }

  public resetUserPassword(
    token: string,
    newPassword: string | null = null,
    reNewPassword: string | null = null
  ): Observable<unknown> {
    return this.injector.get(MtmAuthBridgeService).resetPassword$({
      token,
      newPassword: newPassword ?? '',
      reNewPassword: reNewPassword ?? '',
    });
  }

  public getUserSettings(name: string): string | number | boolean | null {
    const settings: ProfileSettings | null = this.getAuthUserSettings();
    if (!settings) {
      return null;
    }
    const value: string | number | boolean | undefined = settings[name as keyof ProfileSettings];
    return value ?? null;
  }

  public getEffectivePermissions(): EffectivePermissions {
    return this.authPermissionsService.getEffectivePermissions();
  }

  public updateEffectivePermissions(force: boolean = false): Observable<EffectivePermissions> {
    return this.authPermissionsService.updateEffectivePermissions(force);
  }

  public setAuthLanguage(lang: string): void {
    contentLanguageUpdated.emit(lang);
    localStorage.setItem('auth_user_language', lang);
  }

  public getAuthLanguage(useDefault: boolean = true): string | null {
    const lang: string | null = localStorage.getItem('auth_user_language');
    if (lang) {
      return lang;
    }
    return useDefault ? 'en_us' : null;
  }

  public authenticate(data: LoginResponse): Observable<EffectivePermissions> {
    // Save token and user on localStorage
    this.setAuthToken(data.auth.token);
    // Add timestamp
    this.setAuthSession();

    //Add default language
    if (!localStorage.getItem('auth_user_language')) {
      this.setAuthLanguage('en_us');
    }

    this.setAuthUser(data.user);
    if (data.userProfileSettings) {
      this.setAuthUserSettings(data.userProfileSettings);
    }

    return this.updateEffectivePermissions();
  }

  public getMyBrand(): Brand {
    return JSON.parse(localStorage.getItem('brand') || '{}');
  }

  public setMyBrand(brand: Brand): void {
    const previousId: string | null = localStorage.getItem('brandId');
    localStorage.setItem('brandId', brand.id);
    localStorage.setItem('brand', JSON.stringify(brand));
    this.rememberVisitedBrand(brand.id);

    // Targeted patch instead of setAuthUser(): that early-returns when globalRole/companyId are
    // missing and re-runs avatar fixups. The IAM SDK stores its identity under `iam_auth_user`,
    // so writing `auth_user` here cannot clash with it.
    const authUser: User | null = this.getAuthUser();
    if (authUser) {
      authUser.brandId = brand.id;
      localStorage.setItem('auth_user', JSON.stringify(authUser));
    }

    // setMyBrand() runs on every dashboard load, not only on an actual switch — gate the
    // side effects on a real brand change or listeners refire on each page load.
    if (previousId !== brand.id) {
      this.clearBrandScopedStorage();
      EmitterService.get(BrandUpdatedEvent).emit(brand);
    }
  }

  private rememberVisitedBrand(brandId: string): void {
    const username: string | null = this.getAuthUserName();
    if (!username || !brandId) {
      return;
    }
    const visits: Record<string, string> = this.readVisitedBrands();
    if (visits[username] === brandId) {
      return;
    }
    visits[username] = brandId;
    localStorage.setItem(LAST_VISITED_BRAND_STORAGE_KEY, JSON.stringify(visits));
  }

  public getLastVisitedBrandId(username: string | null = this.getAuthUserName()): string | null {
    if (!username) {
      return null;
    }
    return this.readVisitedBrands()[username] ?? null;
  }

  private readVisitedBrands(): Record<string, string> {
    try {
      const raw: string | null = localStorage.getItem(LAST_VISITED_BRAND_STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
    } catch {
      return {};
    }
  }

  /**
   * Drop storage that holds old-brand entity ids. Targeted removes only — never clear():
   * `admin_token` / `admin_user` / `onbehalf_user` must survive or "return to admin" breaks
   * during impersonation.
   */
  private clearBrandScopedStorage(): void {
    ['filterState', 'analyticsParams', 'campaignSearchParams', 'campaignSortBy', 'workspaceId', 'dcId'].forEach(
      (key: string) => localStorage.removeItem(key)
    );
    ['projectId', 'project'].forEach((key: string) => sessionStorage.removeItem(key));
  }

  public getCurrentBrandId(): string {
    return localStorage.getItem('brandId') || '';
  }
}
